/**
 * Mother City Chai - Custom JavaScript
 * Handles cart drawer, product interactions, and theme functionality
 */

class CartDrawer {
  constructor() {
    this.drawer = document.querySelector('.cart-drawer');
    this.overlay = document.querySelector('.cart-drawer-overlay');
    this.closeButtons = document.querySelectorAll('.cart-drawer__close');
    this.cartIcons = document.querySelectorAll('[data-cart-icon]');

    if (this.drawer) {
      this.init();
    }
  }

  init() {
    // Open drawer when cart icon clicked
    this.cartIcons.forEach(icon => {
      icon.addEventListener('click', (e) => {
        e.preventDefault();
        this.open();
      });
    });

    // Close drawer
    this.closeButtons.forEach(btn => {
      btn.addEventListener('click', () => this.close());
    });

    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.close());
    }

    // Handle quantity changes
    this.drawer.addEventListener('change', (e) => {
      if (e.target.classList.contains('cart-item__quantity')) {
        this.updateQuantity(e.target);
      }
    });

    // Handle remove item
    this.drawer.addEventListener('click', (e) => {
      if (e.target.closest('.cart-item__remove')) {
        e.preventDefault();
        this.removeItem(e.target.closest('.cart-item__remove'));
      }
    });
  }

  open() {
    if (!this.drawer) return;
    this.drawer.classList.add('active');
    if (this.overlay) {
      this.overlay.classList.add('active');
    }
    document.body.style.overflow = 'hidden';
    this.fetchCart();
  }

  close() {
    if (!this.drawer) return;
    this.drawer.classList.remove('active');
    if (this.overlay) {
      this.overlay.classList.remove('active');
    }
    document.body.style.overflow = '';
  }

  async fetchCart() {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) return;
      const cart = await response.json();
      this.renderCart(cart);
    } catch (error) {
      console.error('Error fetching cart:', error);
    }
  }

  renderCart(cart) {
    const cartItemsContainer = this.drawer.querySelector('.cart-drawer__items');
    const cartCount = this.drawer.querySelector('.cart-drawer__count');
    const emptyState = this.drawer.querySelector('.cart-drawer__empty');
    const cartFooter = this.drawer.querySelector('.cart-drawer__footer');

    // Update all cart count badges
    this.updateCartBadges(cart.item_count);

    if (cart.item_count === 0) {
      if (emptyState) emptyState.style.display = 'block';
      if (cartFooter) cartFooter.style.display = 'none';
      if (cartItemsContainer) cartItemsContainer.innerHTML = '';
      return;
    }

    if (emptyState) emptyState.style.display = 'none';
    if (cartFooter) cartFooter.style.display = 'block';

    if (cartCount) {
      cartCount.textContent = cart.item_count + (cart.item_count === 1 ? ' item' : ' items');
    }

    // Update subtotal price only (not the label)
    const priceSpan = this.drawer.querySelector('.cart-drawer__subtotal-price');
    if (priceSpan) {
      priceSpan.textContent = this.formatMoney(cart.total_price);
    }

    // Render cart items
    if (cartItemsContainer) {
      cartItemsContainer.innerHTML = cart.items.map(item => this.renderCartItem(item)).join('');
    }

    this.updateFreeShippingBar(cart.total_price);
  }

  renderCartItem(item) {
    const itemPrice = item.final_line_price;
    const compareAtPrice = item.original_line_price;
    const hasDiscount = compareAtPrice > itemPrice;
    const title = this.escapeHtml(item.product_title);
    const variantTitle = item.variant_title ? this.escapeHtml(item.variant_title) : '';

    return '<div class="cart-item" data-line-item="' + item.key + '">' +
      '<a href="' + item.url + '" class="cart-item__image">' +
        '<img src="' + (item.image || '') + '" alt="' + title + '" loading="lazy">' +
      '</a>' +
      '<div class="cart-item__details">' +
        '<a href="' + item.url + '" class="cart-item__title">' + title + '</a>' +
        (variantTitle ? '<div class="cart-item__variant">' + variantTitle + '</div>' : '') +
        '<div class="cart-item__price">' +
          '<span class="cart-item__price-current">' + this.formatMoney(itemPrice) + '</span>' +
          (hasDiscount ? ' <s class="cart-item__price-compare">' + this.formatMoney(compareAtPrice) + '</s>' : '') +
        '</div>' +
        '<div class="cart-item__quantity-wrapper">' +
          '<div class="quantity-selector">' +
            '<button type="button" class="quantity-selector__button" data-action="decrease" data-key="' + item.key + '"><span>−</span></button>' +
            '<input type="number" class="cart-item__quantity quantity-selector__input" value="' + item.quantity + '" min="0" data-key="' + item.key + '">' +
            '<button type="button" class="quantity-selector__button" data-action="increase" data-key="' + item.key + '"><span>+</span></button>' +
          '</div>' +
          '<button type="button" class="cart-item__remove" data-key="' + item.key + '">Remove</button>' +
        '</div>' +
      '</div>' +
    '</div>';
  }

  escapeHtml(str) {
    var div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  async updateQuantity(input) {
    const key = input.dataset.key;
    const quantity = parseInt(input.value);

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: quantity })
      });
      if (!response.ok) return;
      const cart = await response.json();
      this.renderCart(cart);
    } catch (error) {
      console.error('Error updating cart:', error);
    }
  }

  async removeItem(button) {
    const key = button.dataset.key;

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: 0 })
      });
      if (!response.ok) return;
      const cart = await response.json();
      this.renderCart(cart);
    } catch (error) {
      console.error('Error removing item:', error);
    }
  }

  updateFreeShippingBar(totalPrice) {
    const freeShippingBar = this.drawer.querySelector('.free-shipping-bar');
    if (!freeShippingBar) return;

    const threshold = parseFloat(freeShippingBar.dataset.threshold) * 100;
    const progress = Math.min((totalPrice / threshold) * 100, 100);
    const remaining = Math.max(threshold - totalPrice, 0);

    const progressBar = freeShippingBar.querySelector('.free-shipping-bar__progress');
    const message = freeShippingBar.querySelector('.free-shipping-bar__message');

    if (progressBar) {
      progressBar.style.width = progress + '%';
    }

    if (message) {
      if (progress >= 100) {
        message.textContent = 'You qualify for free shipping!';
      } else {
        message.textContent = 'Add ' + this.formatMoney(remaining) + ' more for free shipping';
      }
    }
  }

  updateCartBadges(itemCount) {
    var configs = [
      { parent: '.hero-floating-menu__cart', cls: 'hero-floating-menu__cart-count' },
      { parent: '.header__cart-icon', cls: 'header__cart-count' }
    ];

    configs.forEach(function(cfg) {
      var parentEl = document.querySelector(cfg.parent);
      if (!parentEl) return;

      var badge = parentEl.querySelector('.' + cfg.cls);

      if (itemCount > 0) {
        if (!badge) {
          badge = document.createElement('span');
          badge.className = cfg.cls;
          parentEl.appendChild(badge);
        }
        badge.textContent = itemCount;
        badge.style.display = 'flex';
      } else if (badge) {
        badge.style.display = 'none';
      }
    });
  }

  formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2) + ' AUD';
  }
}

// Quick Add to Cart — uses form POST as fallback-safe approach
class QuickAdd {
  constructor() {
    // Bind to all quick-add buttons directly
    document.querySelectorAll('[data-quick-add]').forEach(function(button) {
      button.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();
        QuickAdd.addToCart(button);
      });
    });
  }

  static async addToCart(button) {
    if (button.dataset.adding === 'true') return;

    var variantId = button.dataset.productId;
    if (!variantId) return;

    var originalText = button.textContent.trim();
    button.dataset.adding = 'true';
    button.textContent = 'Adding...';

    try {
      var response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: Number(variantId), quantity: 1 })
      });

      if (response.ok) {
        button.textContent = 'Added!';
        if (window.cartDrawer) {
          window.cartDrawer.open();
        }
      } else {
        // Don't try to parse non-JSON error responses
        console.error('Cart add failed:', response.status);
        button.textContent = 'Error';
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      button.textContent = 'Error';
    }

    setTimeout(function() {
      button.textContent = originalText;
      button.dataset.adding = 'false';
    }, 2000);
  }
}

// Quantity selector (for cart drawer + product page)
class QuantitySelector {
  constructor() {
    document.addEventListener('click', function(e) {
      var button = e.target.closest('.quantity-selector__button');
      if (!button) return;

      var action = button.dataset.action;
      var key = button.dataset.key;

      // Cart drawer quantity buttons (have data-key)
      if (key) {
        var input = document.querySelector('input[data-key="' + key + '"]');
        if (!input) return;

        var val = parseInt(input.value);
        if (action === 'increase') {
          input.value = val + 1;
        } else if (action === 'decrease' && val > 1) {
          input.value = val - 1;
        }
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }

      // Product page quantity buttons
      if (action === 'increase-product' || action === 'decrease-product') {
        var input2 = document.getElementById('product-quantity');
        if (!input2) return;

        var val2 = parseInt(input2.value);
        if (action === 'increase-product') {
          input2.value = val2 + 1;
        } else if (val2 > 1) {
          input2.value = val2 - 1;
        }
      }
    });
  }
}

// Product Form — AJAX add-to-cart + variant selection
class ProductForm {
  constructor() {
    this.form = document.querySelector('[data-product-form]');
    if (!this.form) return;

    this.submitButton = this.form.querySelector('.product-form__submit');
    this.variantInput = this.form.querySelector('#product-variant-id');
    this.variantsJson = document.getElementById('product-variants-json');
    this.variants = this.variantsJson ? JSON.parse(this.variantsJson.textContent) : [];

    this.init();
  }

  init() {
    var self = this;

    this.form.addEventListener('submit', function(e) {
      e.preventDefault();
      self.addToCart();
    });

    this.form.querySelectorAll('.product-option__pill').forEach(function(pill) {
      pill.addEventListener('click', function() { self.selectVariant(pill); });
    });
  }

  selectVariant(pill) {
    var value = pill.dataset.optionValue;
    var optionGroup = pill.closest('.product-option');
    optionGroup.querySelectorAll('.product-option__pill').forEach(function(p) { p.classList.remove('active'); });
    pill.classList.add('active');

    var select = optionGroup.querySelector('.product-option__select');
    if (select) select.value = value;

    var selectedOptions = [];
    this.form.querySelectorAll('.product-option').forEach(function(group) {
      var activePill = group.querySelector('.product-option__pill.active');
      if (activePill) selectedOptions.push(activePill.dataset.optionValue);
    });

    var matchedVariant = this.variants.find(function(variant) {
      return selectedOptions.every(function(opt, i) { return variant.options[i] === opt; });
    });

    if (matchedVariant) {
      this.variantInput.value = matchedVariant.id;

      // Update price display safely
      var priceEl = document.getElementById('product-price');
      if (priceEl) {
        while (priceEl.firstChild) priceEl.removeChild(priceEl.firstChild);

        var price = (matchedVariant.price / 100).toFixed(2);
        var priceSpan = document.createElement('span');
        priceSpan.className = 'price';
        priceSpan.textContent = '$' + price + ' AUD';

        if (matchedVariant.compare_at_price && matchedVariant.compare_at_price > matchedVariant.price) {
          priceSpan.classList.add('price--on-sale');
          priceEl.appendChild(priceSpan);
          priceEl.appendChild(document.createTextNode(' '));
          var compareSpan = document.createElement('s');
          compareSpan.className = 'price price--compare';
          compareSpan.textContent = '$' + (matchedVariant.compare_at_price / 100).toFixed(2) + ' AUD';
          priceEl.appendChild(compareSpan);
        } else {
          priceEl.appendChild(priceSpan);
        }
      }

      if (matchedVariant.available) {
        this.submitButton.disabled = false;
        this.submitButton.textContent = 'Add to Cart';
      } else {
        this.submitButton.disabled = true;
        this.submitButton.textContent = 'Sold Out';
      }
    }
  }

  async addToCart() {
    var variantId = this.variantInput.value;
    var quantityInput = this.form.querySelector('#product-quantity');
    var quantity = quantityInput ? parseInt(quantityInput.value) : 1;

    var cartData = { id: Number(variantId), quantity: quantity };
    var sellingPlanInput = this.form.querySelector('[name="selling_plan"]');
    if (sellingPlanInput && sellingPlanInput.value) {
      cartData.selling_plan = Number(sellingPlanInput.value);
    }

    var originalText = this.submitButton.textContent;
    this.submitButton.textContent = 'Adding...';
    this.submitButton.disabled = true;

    try {
      var response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(cartData)
      });

      if (response.ok) {
        this.submitButton.textContent = 'Added!';
        if (window.cartDrawer) {
          window.cartDrawer.open();
        }
        var self = this;
        setTimeout(function() {
          self.submitButton.textContent = originalText;
          self.submitButton.disabled = false;
        }, 2000);
      } else {
        console.error('Cart add failed:', response.status);
        throw new Error('Failed');
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      this.submitButton.textContent = 'Error';
      var self = this;
      setTimeout(function() {
        self.submitButton.textContent = originalText;
        self.submitButton.disabled = false;
      }, 2000);
    }
  }
}

// FAQ Accordion
class FAQAccordion {
  constructor() {
    document.querySelectorAll('.faq-item__question').forEach(function(question) {
      question.addEventListener('click', function() {
        var item = question.parentElement;
        document.querySelectorAll('.faq-item').forEach(function(otherItem) {
          if (otherItem !== item) otherItem.classList.remove('active');
        });
        item.classList.toggle('active');
      });
    });
  }
}

// Tabs functionality
class Tabs {
  constructor() {
    document.querySelectorAll('[data-tab]').forEach(function(tab) {
      tab.addEventListener('click', function() {
        var tabGroup = tab.closest('[data-tabs]');
        var targetContent = document.getElementById(tab.dataset.tab);
        if (!targetContent) return;

        tabGroup.querySelectorAll('[data-tab]').forEach(function(t) { t.classList.remove('active'); });
        tabGroup.querySelectorAll('.tab-content').forEach(function(c) { c.classList.remove('active'); });

        tab.classList.add('active');
        targetContent.classList.add('active');
      });
    });
  }
}

// Buy Now — direct checkout
class BuyNow {
  constructor() {
    var buyNowBtn = document.getElementById('product-buy-now');
    if (!buyNowBtn) return;

    buyNowBtn.addEventListener('click', function() {
      var variantId = document.getElementById('product-variant-id');
      var quantityInput = document.getElementById('product-quantity');
      if (!variantId) return;

      var id = Number(variantId.value);
      var quantity = quantityInput ? parseInt(quantityInput.value) : 1;

      var cartData = { id: id, quantity: quantity };
      var sellingPlanInput = document.querySelector('[data-product-form] [name="selling_plan"]');
      if (sellingPlanInput && sellingPlanInput.value) {
        cartData.selling_plan = Number(sellingPlanInput.value);
      }

      buyNowBtn.textContent = 'Processing...';
      buyNowBtn.disabled = true;

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(cartData)
      })
      .then(function(response) {
        if (response.ok) {
          window.location.href = '/checkout';
        } else {
          throw new Error('Failed');
        }
      })
      .catch(function() {
        buyNowBtn.textContent = 'Buy Now';
        buyNowBtn.disabled = false;
      });
    });
  }
}

// Subscription Rules — mobile tap toggle
class SubscriptionRules {
  constructor() {
    var triggers = document.querySelectorAll('.subscription-rules__trigger');
    if (!triggers.length) return;

    var self = this;
    triggers.forEach(function(trigger) {
      trigger.addEventListener('click', function(e) {
        if (window.matchMedia('(hover: hover)').matches) return;
        e.preventDefault();
        self.toggle(trigger);
      });
    });

    document.addEventListener('click', function(e) {
      if (!e.target.closest('.subscription-rules')) {
        self.closeAll();
      }
    });
  }

  toggle(trigger) {
    var popup = trigger.nextElementSibling;
    var isOpen = popup.classList.contains('active');
    this.closeAll();
    if (!isOpen) {
      popup.classList.add('active');
      trigger.setAttribute('aria-expanded', 'true');
    }
  }

  closeAll() {
    document.querySelectorAll('.subscription-rules__popup.active').forEach(function(p) {
      p.classList.remove('active');
      p.previousElementSibling.setAttribute('aria-expanded', 'false');
    });
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  window.cartDrawer = new CartDrawer();
  new QuickAdd();
  new QuantitySelector();
  new ProductForm();
  new BuyNow();
  new FAQAccordion();
  new Tabs();
  new SubscriptionRules();
});
